import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
    fetchBranches,
    fetchDoctors,
    selectBranch,
    selectDate,
    selectDoctor,
    selectChildrenFlag,
    selectSpecialization,
} from './filtersSlice';
import { selectFiltersMetadata } from './selectors';
import { GroupInput } from '../../components/GroupInput/GroupInput';
import { useWidgetProps } from '../../WidgetPropsContext';
import { getAssigmentsInfo } from '../../api';

import './filters.scss';
import { selectTimeslots } from '../doctors/selectors';
import { convertTimeZone, formatFullDate, SERVER_TIMEZONE } from '../../utils';
import { findDoctorById } from './utils';
import { clearTimetableRequestParams, fetchTimeslots, fetchTimetable } from '../doctors/doctorsSlice';
import { clearDoctorBookingError } from '../bookingForm/bookingFormSlice';
import { DoctorFilters } from './DoctorFilters';
import { ForChildSwitch } from '../../components/ForChildSwitch/ForChildSwitch';
import { AgeSelector } from '../../components/AgeSelector/AgeSelector';

const filters = require('b_')
    .with('filters');

const START_TO_TYPE = 'Начните вводить данные';

const addMonths = (date, months) => {
    const monthSum = date.getMonth() + months;
    const newMonth = (12 + (monthSum % 12)) % 12;
    const newYear = date.getFullYear() + Math.floor(monthSum / 12);
    const newDate = new Date(newYear, newMonth, date.getDate());
    return (newDate.getMonth() !== newMonth)
        ? new Date(newDate.setDate(0))
        : newDate;
};
const getEndDate = (d) => {
    return addMonths(convertTimeZone(d, null, SERVER_TIMEZONE), 2);
};

// eslint-disable-next-line max-statements
export const Filters = ({
    isTimeslotOccupied = false,
    error: _error,
}) => {
    const {
        baseUrl,
        doctorId: _doctorId,
        branchId: _branchId,
        currentBranchId,
        assignmentIds: _assignmentIds,
    } = useWidgetProps();
    const dispatch = useDispatch();
    const {
        pathname,
        state = {},
    } = useLocation();
    const history = useHistory();
    const [ assignments, setAssignments ] = useState();

    const {
        isLoading,
        error,
        branches,
        doctors,
        doctorsData,
        specializations,
        selectedBranch,
        selectedDoctor,
        selectedSpecialization,
        date,
        fulldoctors,
        forChild,
        childrenAge,
    } = useSelector(selectFiltersMetadata);

    const doctorsFromBranch = useCallback(() => {
        return [...(doctors ?? [])].filter(({ id }) => fulldoctors.find(
            ({
                doctorId = '',
                branches: b = [],
            }) => doctorId === id &&
                b.includes(selectedBranch),
        ));
    }, [ doctors, fulldoctors, selectedBranch ]);

    const timetable = useSelector(selectTimeslots);

    useEffect(() => {
        if (baseUrl) {
            if (state.reloadBranches !== false && (currentBranchId || currentBranchId === null)) {
                dispatch(
                    fetchBranches({
                        baseUrl,
                        currentBranchId,
                    }),
                );
                dispatch(
                    fetchDoctors({
                        baseUrl,
                        currentBranchId,
                    }),
                );
            }
        }
    }, [ baseUrl, currentBranchId, dispatch ]);

    useEffect(async () => {
        if (_assignmentIds && !selectedDoctor) {
            const info = await getAssigmentsInfo(_assignmentIds);
            if (info && info.length) {
                setAssignments(info);
            }
        }
    }, [_assignmentIds]);
    useEffect(() => {
        if (branches?.length === 1 && !selectedBranch && doctors?.length) {
            const id = branches?.[0]?.id;
            dispatch(selectBranch(id));
        }
    }, [ branches, date, baseUrl, doctors ]);

    const redirectToTheHomePage = useCallback((samePage = false) => {
        const isHomePathname = pathname.length <= 1;
        dispatch(clearDoctorBookingError());
        if (samePage) {
            history.push(pathname, {
                ...state,
                errorMessage: undefined,
            });
        } else if (!isHomePathname) {
            history.push('/', {
                ...state,
                errorMessage: undefined,
            });
        }
    }, [ history, pathname.length, state ]);

    const onDoctorSelect = useCallback(
        (doctorId) => {
            if (!selectedSpecialization) {
                const specializationId = doctorsData.find(({ doctorId: docId }) => docId === doctorId)?.specialityId;
                dispatch(fetchTimeslots({
                    branch: selectedBranch,
                    date,
                    baseUrl,
                    specializationId,
                }));
            }
            dispatch(selectDoctor(doctorId));
            redirectToTheHomePage();
        },
        [ dispatch, redirectToTheHomePage, selectedSpecialization ],
    );

    useEffect(() => {
        if (_doctorId && !isLoading && !selectedDoctor) {
            dispatch(selectBranch(_branchId));
            dispatch(selectDoctor(_doctorId));
            const specializationId = fulldoctors.find(({ doctorId: di }) => di === _doctorId)?.specialityId;
            dispatch(fetchTimeslots({
                branch: _branchId,
                date,
                baseUrl,
                specializationId,
            }));
        } else if (assignments && !isLoading) {
            const foundDoctor =
                assignments.find(assignment => fulldoctors.some(
                    doctor => doctor.doctorId === assignment.doctorId &&
                        doctor.branches.some(branch => branch === assignment.branchId),
                )) || {};
            const {
                branchId: abranchId,
                doctorId: adoctorId,
            } = foundDoctor;
            if (abranchId && adoctorId) {
                dispatch(selectBranch(abranchId));
                dispatch(selectDoctor(adoctorId));
            }
        }
    }, [ _branchId, _doctorId, assignments, dispatch, isLoading ]);

    const onBranchSelect = useCallback(
        (branchId) => {
            dispatch(selectBranch(branchId));
            dispatch(clearTimetableRequestParams());
            redirectToTheHomePage();
        },
        [ dispatch, redirectToTheHomePage ],
    );

    const onSpecializationSelect = useCallback(
        (specializationId) => {
            dispatch(selectSpecialization(specializationId));
            redirectToTheHomePage();
        },
        [ dispatch, redirectToTheHomePage ],
    );

    const onChildSelect = useCallback(
        (child, age) => {
            dispatch(selectChildrenFlag({ forChild: child, childrenAge: age }));
            redirectToTheHomePage(false);
        },
        [ dispatch, redirectToTheHomePage ],
    );

    const onDateSelect = useCallback(
        (dateValue) => {
            dispatch(selectDate(dateValue));
            redirectToTheHomePage();
        },
        [ dispatch, redirectToTheHomePage, selectedSpecialization, selectedDoctor, selectedBranch ],
    );

    const loadTimeTable = async (doctorId, branchId, isTimeslotOccupied = false) => {
        const dateStart = formatFullDate(convertTimeZone(new Date(Date.now()), null, SERVER_TIMEZONE));
        const dateEnd = formatFullDate(getEndDate(new Date(Date.now())));
        const loadedString = `${doctorId};${dateStart};${dateEnd}`;
        if (doctorId && branchId && !(loadedString === timetable?.timetableLoaded && !isTimeslotOccupied)) {
            const doctor = findDoctorById(fulldoctors, doctorId);
            const doctorAssignments = doctor?.assignments ?? [];
            try {
                let assignment = doctorAssignments[0];
                if (doctorAssignments.length > 1) {
                    try {
                        assignment = (await getAssigmentsInfo(doctorAssignments))
                            ?.find(item => item?.branchId === branchId)?.assignmentId;
                    } catch (e) {
                        // console.log('Error get assignments', e);
                    }
                }
                dispatch(
                    fetchTimetable({
                        assignment,
                        doctorId,
                        dateStart,
                        dateEnd,
                        baseUrl,
                    }),
                );
            } catch (e) {
                //
            }
        }
    };

    const onOnlineState = () => {
        redirectToTheHomePage();
    };

    useEffect(() => {
        // eslint-disable-next-line no-undef
        window?.addEventListener('online', onOnlineState);
        // eslint-disable-next-line no-undef
        return () => window?.removeEventListener('online', onOnlineState);
    }, []);

    useEffect(() => {
        if (fulldoctors && selectedDoctor && selectedBranch && (!_error || _error && isTimeslotOccupied)) {
            loadTimeTable(selectedDoctor, selectedBranch, isTimeslotOccupied);
        }
    }, [ fulldoctors?.length, selectedDoctor, selectedBranch, baseUrl, isTimeslotOccupied, _error, timetable?.timetableLoaded ]);
    return (
        <div className={filters()}>
            {fulldoctors.some(doctor => doctor.children === true) && (
                <div className={filters('child')}>
                    <ForChildSwitch
                        value={forChild}
                        label="Врач принимает детей"
                        onChange={value => onChildSelect(value, childrenAge)}
                    />
                    <div className={filters('child-age')} aria-hidden={!forChild}>
                        <AgeSelector
                            age={childrenAge}
                            label="полных лет"
                            onChange={age => onChildSelect(true, age)}
                        />
                    </div>
                </div>
            )}
            <GroupInput
                id="filter_branch"
                type="selector"
                label="Филиал"
                onChange={onBranchSelect}
                onClear={() => onBranchSelect(undefined)}
                isLoading={isLoading}
                metaData={branches}
                value={selectedBranch}
                error={error}
                placeholder={START_TO_TYPE}
            />
            <GroupInput
                id="filter_specializations"
                type="selector"
                label="Специальность"
                onChange={onSpecializationSelect}
                onClear={() => onSpecializationSelect(undefined)}
                isLoading={isLoading}
                metaData={specializations}
                value={selectedSpecialization}
                error={error}
                placeholder={START_TO_TYPE}
            />
            <GroupInput
                id="filter_doctors"
                type="selector"
                label="Специалист"
                onChange={onDoctorSelect}
                onClear={() => onDoctorSelect(undefined)}
                isLoading={isLoading}
                metaData={doctorsFromBranch()}
                value={selectedDoctor}
                error={error}
                placeholder={START_TO_TYPE}
            />
            <GroupInput
                id="filter_date"
                type="datePicker"
                label="Дата"
                onChange={onDateSelect}
                value={date}
                highlightedDates={timetable?.timetable?.[selectedDoctor] ?? []}
                highlightDates={!!selectedDoctor}
            />
        </div>
    );
};

let isDoctorBookingEnabled = true;

export const FiltersSection = ({ isTimeslotOccupied, error }) => {
    const { doctorName, specialityName, targetDoctorId } = useWidgetProps();
    const [ isDoctorBooking, setIsDoctorBooking ] = useState((!!doctorName || !!targetDoctorId) && isDoctorBookingEnabled);

    const onBack = () => {setIsDoctorBooking(false); isDoctorBookingEnabled = false; };

    useEffect(() => {
        setIsDoctorBooking((!!doctorName || !!targetDoctorId) && isDoctorBookingEnabled);
    }, [ doctorName, targetDoctorId ]);

    return isDoctorBooking ? <DoctorFilters onBack={onBack} doctorName={doctorName} targetDoctorId={targetDoctorId} specialityName={specialityName} /> : <Filters isTimeslotOccupied={isTimeslotOccupied} error={error}/>;
};
